// Demande d'aide média : formulaire ouvert à tous → carte Kanban + webhook media_request.created
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, ExternalLink, HandHelping } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DatePickerFr } from "@/components/DatePickerFr";
import { POLES, PRIORITES, TYPES_DEMANDE_MEDIA, STATUTS_DEMANDE_MEDIA } from "@/lib/constants";
import { formatJour } from "@/lib/dates";

const FORM_VIDE = {
  titre: "", pole: "asbl", type_demande: "affiche", description: "",
  echeance_souhaitee: "", urgence: "normale", lien_reference: "",
};

export default function MediaRequests() {
  const { user } = useAuth();
  const [form, setForm] = useState(FORM_VIDE);
  const [demandes, setDemandes] = useState([]);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/media-requests");
      setDemandes(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await api.post("/media-requests", {
        ...form,
        echeance_souhaitee: form.echeance_souhaitee || null,
        lien_reference: form.lien_reference || null,
      });
      toast.success("Demande envoyée — le pôle Média la reçoit dans son Kanban");
      setForm(FORM_VIDE);
      charger();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoi(false);
    }
  };

  const maj = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-8" data-testid="media-requests-page">
      <div>
        <h1 className="font-serif text-4xl font-bold">Demande d'aide média</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Ton pôle a besoin d'une affiche, d'une vidéo ou d'une captation ? Décris ta demande, elle arrive
          directement dans le Kanban du pôle Média.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={envoyer} className="lg:col-span-3 bg-card rounded-2xl border border-border p-6 space-y-5 carte-douce">
          <div>
            <Label>Objet de la demande *</Label>
            <Input
              required minLength={3} value={form.titre}
              onChange={(e) => maj("titre")(e.target.value)}
              placeholder="Ex. Affiche pour le gala Twige du 12 septembre"
              className="rounded-xl mt-1.5" data-testid="media-request-titre-input"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Pôle demandeur *</Label>
              <Select value={form.pole} onValueChange={maj("pole")}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="media-request-pole-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(POLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type de besoin *</Label>
              <Select value={form.type_demande} onValueChange={maj("type_demande")}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="media-request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPES_DEMANDE_MEDIA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Description détaillée *</Label>
            <Textarea
              required minLength={10} rows={5} value={form.description}
              onChange={(e) => maj("description")(e.target.value)}
              placeholder="Contexte, message à faire passer, formats attendus, textes à intégrer…"
              className="rounded-xl mt-1.5" data-testid="media-request-description-input"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Échéance souhaitée</Label>
              <div className="mt-1.5">
                <DatePickerFr
                  value={form.echeance_souhaitee}
                  onChange={maj("echeance_souhaitee")}
                  placeholder="Pas d'échéance"
                  testId="media-request-echeance-input"
                />
              </div>
            </div>
            <div>
              <Label>Urgence</Label>
              <Select value={form.urgence} onValueChange={maj("urgence")}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="media-request-urgence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lien de référence</Label>
              <Input
                value={form.lien_reference}
                onChange={(e) => maj("lien_reference")(e.target.value)}
                placeholder="Drive, exemple…"
                className="rounded-xl mt-1.5" data-testid="media-request-lien-input"
              />
            </div>
          </div>

          <Button type="submit" disabled={envoi} className="rounded-full gap-2" data-testid="media-request-submit-button">
            <Send className="h-4 w-4" /> {envoi ? "Envoi…" : "Envoyer la demande"}
          </Button>
        </form>

        <div className="lg:col-span-2 space-y-3">
          <h2 className="font-serif text-lg md:text-lg font-semibold">
            {["admin", "media", "ca"].includes(user.role) ? "Toutes les demandes" : "Mes demandes"}
          </h2>
          <div className="space-y-2" data-testid="media-requests-list">
            {demandes.map((d) => (
              <div key={d.id} className="bg-card rounded-2xl border border-border p-4 carte-douce" data-testid={`media-request-row-${d.id}`}>
                <div className="flex items-start gap-2">
                  <HandHelping className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{d.titre}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {POLES[d.pole]} · {TYPES_DEMANDE_MEDIA[d.type_demande]} · {formatJour(d.created_at)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={`${STATUTS_DEMANDE_MEDIA[d.statut]?.classe || ""} border-0 text-[10px]`}>
                        {STATUTS_DEMANDE_MEDIA[d.statut]?.label || d.statut}
                      </Badge>
                      {d.board_id && (
                        <Link
                          to={`/projets/${d.board_id}`}
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                          data-testid={`media-request-board-link-${d.id}`}
                        >
                          Voir dans le Kanban <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {demandes.length === 0 && (
              <p className="text-sm text-muted-foreground py-6" data-testid="media-requests-empty">
                Aucune demande pour l'instant.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
